import { BluetoothServiceCallbacks } from '../types';

// Bluetooth UUIDs - Nordic UART Service
const SERVICE_UUID = '6e400001-b5a3-f393-e0a9-e50e24dcca9e'; // Nordic UART Service
const CHARACTERISTIC_TX_UUID = '6e400002-b5a3-f393-e0a9-e50e24dcca9e'; // TX (Write to device)
const CHARACTERISTIC_RX_UUID = '6e400003-b5a3-f393-e0a9-e50e24dcca9e'; // RX (Receive from device)

class BluetoothService implements BluetoothServiceCallbacks {
  private device: BluetoothDevice | null = null;
  private server: BluetoothRemoteGATTServer | null = null;
  private service: BluetoothRemoteGATTService | null = null;
  private characteristic: BluetoothRemoteGATTCharacteristic | null = null;
  private rxCharacteristic: BluetoothRemoteGATTCharacteristic | null = null;
  private _receivedData: string = '';
  
  public onDisconnected?: () => void;
  public onDataReceived?: (data: string) => void;

  // Check if Web Bluetooth is supported by the browser
  isSupported(): boolean {
    return navigator.bluetooth ? true : false;
  }

  // Request Bluetooth device and connect to the specified service
  async connect(): Promise<boolean> {
    try {
      this.device = await navigator.bluetooth!.requestDevice({
        acceptAllDevices: true,
        optionalServices: [
          SERVICE_UUID,
          '6e400001-b5a3-f393-e0a9-e50e24dcca9e', // Nordic UART Service
          '6e400002-b5a3-f393-e0a9-e50e24dcca9e', // Nordic UART TX
          '6e400003-b5a3-f393-e0a9-e50e24dcca9e', // Nordic UART RX
          '00001800-0000-1000-8000-00805f9b34fb', // Generic Access
          '0000180a-0000-1000-8000-00805f9b34fb'  // Device Information
        ]
      });

      this.server = await this.device.gatt!.connect();
      
      // Set up device disconnection handler
      this.device.addEventListener('gattserverdisconnected', this._handleDisconnected.bind(this));

      this.service = await this.server.getPrimaryService(SERVICE_UUID);

      this.characteristic = await this.service.getCharacteristic(CHARACTERISTIC_TX_UUID);
      
      this.rxCharacteristic = await this.service.getCharacteristic(CHARACTERISTIC_RX_UUID);
      
      // Start notifications on RX characteristic
      if (this.rxCharacteristic.properties.notify) {
        await this.rxCharacteristic.startNotifications();
        this.rxCharacteristic.addEventListener('characteristicvaluechanged', 
          this._handleCharacteristicValueChanged.bind(this));
      }
      return true;
    } catch (error) {
      this.disconnect();
      throw error;
    }
  }

  // Disconnect from the device
  disconnect(): void {
    if (this.device && this.device.gatt!.connected) {
      if (this.rxCharacteristic && this.rxCharacteristic.properties.notify) {
        this.rxCharacteristic.stopNotifications();
      }
      this.device.gatt!.disconnect();
    }
    this._cleanup();
  }

  // Send data to the connected device with chunking for long strings
  async sendCommand(command: string): Promise<boolean> {
    if (!this.characteristic) {
      throw new Error('Not connected to a device');
    }

    const encoder = new TextEncoder();
    // Use \r\n terminator (firmware responds with this, even if "Unknown command")
    const fullData = encoder.encode(command + '\r\n'); // Add CR+LF terminator
    const chunkSize = 20; // BLE MTU limit, conservative estimate
    
    // Split into chunks if needed
    if (fullData.length <= chunkSize) {
      await this.characteristic.writeValue(fullData);
    } else {
      for (let i = 0; i < fullData.length; i += chunkSize) {
        const chunk = fullData.slice(i, i + chunkSize);
        await this.characteristic.writeValue(chunk);
      }
    }
    return true;
  }

  // Handle device disconnection
  private _handleDisconnected(): void {
    this._cleanup();
    if (this.onDisconnected) {
      this.onDisconnected();
    }
  }

  // Handle incoming data from the device with chunking support
  private _handleCharacteristicValueChanged(event: Event): void {
    const target = event.target as BluetoothRemoteGATTCharacteristic;
    const value = target.value;
    if (!value) {
      return;
    }

    // Convert received data to string and filter out null bytes
    const decoder = new TextDecoder('utf-8');
    const chunk = decoder.decode(value).replace(/\0/g, ''); // Remove null bytes

    // Accumulate chunks until newline
    this._receivedData += chunk;

    if (chunk.includes('\n')) {
      // Clean up the response: remove null bytes, trim whitespace
      const fullResponse = this._receivedData
        .replace(/\0/g, '')  // Remove any remaining null bytes
        .replace(/\r\n/g, '\n')  // Normalize line endings
        .trim();
      
      this._receivedData = ''; // Reset for next message

      if (this.onDataReceived) {
        this.onDataReceived(fullResponse);
      }
    }
  }

  // Clean up resources
  private _cleanup(): void {
    this._receivedData = ''; // Reset received data buffer
    
    if (this.rxCharacteristic && this.rxCharacteristic.properties.notify) {
      this.rxCharacteristic.removeEventListener('characteristicvaluechanged', 
        this._handleCharacteristicValueChanged);
    }
    
    if (this.device) {
      this.device.removeEventListener('gattserverdisconnected', 
        this._handleDisconnected.bind(this));
    }
    
    this.device = null;
    this.server = null;
    this.service = null;
    this.characteristic = null;
    this.rxCharacteristic = null;
  }
}

// Export a singleton instance
export const bluetoothService = new BluetoothService();
